<script setup lang="ts">
import { computed } from 'vue'
import CollapsibleCard from './CollapsibleCard.vue'
import type { StepAsideView, StepAsideStatus } from '../../composables/useSignalPipeline'

// Futures rule card — the decision story for pure step-aside presets
// (JH jh_futures_driven since 2026-07-27, BGA bga_futures_driven_v1 since
// 2026-08-29). Fund-agnostic: everything here comes from the step_aside_*
// payload fields, never from fund-specific columns.
const props = withDefaults(defineProps<{
  stepAside: StepAsideView | null
  status: StepAsideStatus | null
  printSource?: string
}>(), { printSource: 'Databento NQ' })

const fmtPx = (v: number | null | undefined) =>
  v != null ? v.toLocaleString('en-GB', { maximumFractionDigits: 2 }) : '—'

const bps = computed(() => props.stepAside?.sOn != null ? props.stepAside.sOn * 10000 : null)
const pct = computed(() => {
  const s = props.stepAside?.sOn
  if (s == null) return null
  return `${s >= 0 ? '+' : ''}${(s * 100).toFixed(2)}%`
})
const bpsLabel = computed(() => bps.value != null ? `${bps.value >= 0 ? '+' : ''}${Math.round(bps.value)} bps` : null)
const threshold = computed(() => props.stepAside?.thresholdBps ?? 0)
const clears = computed(() => bps.value != null ? bps.value >= threshold.value : null)
const degraded = computed(() => props.status?.state === 'degraded' || bps.value == null)

const summary = computed(() => {
  const state = props.status?.label ? ` · ${props.status.label}` : ''
  if (bps.value == null) return `no NQ print — stays invested${state}`
  return `NQ ${pct.value} (${bpsLabel.value}) · ${clears.value ? 'clears' : 'below'} ${threshold.value} bps${state}`
})

// Threshold bar: the bar spans ±25 bps around the threshold (2% of width
// per bp, clamped) — enough to see a 5 bp clear as "just over the line".
const markerLeft = computed(() => {
  if (bps.value == null) return 50
  const off = Math.max(-48, Math.min(48, (bps.value - threshold.value) * 2))
  return 50 + off
})

const frozenAtLabel = computed(() => {
  const at = props.stepAside?.frozenAt
  if (!at) return null
  const d = new Date(at)
  if (isNaN(d.getTime())) return null
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/London' })
})

const ruleText = computed(() => threshold.value === 0
  ? 'in unless the 08:40 NQ print is below the prev close'
  : `in unless the 08:40 NQ print is more than ${Math.abs(threshold.value)} bps ${threshold.value < 0 ? 'below' : 'above'} the prev close`)
</script>

<template>
  <CollapsibleCard title="Futures rule" testid="futures-rule-card" :summary="summary" :alert="degraded">
    <div class="fr-rule">Rule: {{ ruleText }}.</div>

    <div class="fr-inputs">
      <div class="fr-input"><div class="fr-label">prev close</div><div class="fr-value">{{ fmtPx(stepAside?.nqPrevClose) }}</div></div>
      <div class="fr-input"><div class="fr-label">08:40 print</div><div class="fr-value">{{ fmtPx(stepAside?.nqPrint) }}</div></div>
      <div class="fr-input"><div class="fr-label">overnight</div>
        <div class="fr-value" :class="bps == null ? '' : clears ? 'fr-pos' : 'fr-neg'">{{ pct ?? '—' }}<span v-if="bpsLabel" class="fr-bps"> ({{ bpsLabel }})</span></div></div>
    </div>

    <div class="fr-bar">
      <div class="fr-thr" :title="`${threshold} bps bar`"></div>
      <div v-if="bps != null" class="fr-marker" data-testid="futures-rule-marker"
           :class="clears ? 'mk-buy' : 'mk-sell'" :style="{ left: markerLeft + '%' }"></div>
    </div>
    <div class="fr-scale"><span class="fr-neg">SELL ←</span><span>{{ threshold }} bps</span><span class="fr-pos">→ BUY</span></div>

    <div class="fr-meta">
      <span>print source: {{ printSource }}</span>
      <span v-if="status" class="fr-pill" :class="'pill-' + status.state">{{ status.label }}</span>
      <span v-if="frozenAtLabel" class="fr-dim">latched {{ frozenAtLabel }} London</span>
      <span v-if="degraded" class="fr-dim">degraded inputs fail-safe to stay invested</span>
    </div>
    <div class="fr-note">Sells settle T+1 at HL — a SELL today is cash from tomorrow's strike; the band signal below the pipeline is context only.</div>
  </CollapsibleCard>
</template>

<style scoped>
.fr-rule { font-size: .75rem; color: #c9d1d9; margin-bottom: 6px; }
.fr-inputs { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 8px; }
.fr-label { font-size: .6rem; color: #6e7681; text-transform: uppercase; }
.fr-value { font-family: 'JetBrains Mono', monospace; font-size: .85rem; color: #e6edf3; }
.fr-bps { font-size: .7rem; color: #8b949e; }
.fr-pos { color: #3fb950; } .fr-neg { color: #f85149; }
.fr-bar { position: relative; height: 8px; background: #21262d; border-radius: 4px; margin: 6px 0 2px; }
.fr-thr { position: absolute; left: 50%; top: -3px; width: 2px; height: 14px; background: #d29922; }
.fr-marker { position: absolute; top: -2px; width: 12px; height: 12px; border-radius: 6px; transform: translateX(-50%); }
.mk-buy { background: #3fb950; } .mk-sell { background: #f85149; }
.fr-scale { display: flex; justify-content: space-between; font-size: .6rem; color: #6e7681; margin-bottom: 6px; }
.fr-meta { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; font-size: .7rem; color: #8b949e; }
.fr-dim { color: #6e7681; }
.fr-pill { font-size: .62rem; padding: 0 7px; border-radius: 9px; border: 1px solid #30363d; }
.pill-live { color: #3fb950; border-color: #238636; background: #23863622; }
.pill-frozen { color: #58a6ff; border-color: #1f6feb; background: #1f6feb22; }
.pill-degraded { color: #d29922; border-color: #9e6a03; background: #9e6a0322; }
.fr-note { font-size: .68rem; color: #6e7681; margin-top: 6px; }
</style>
